export const SKYBRIDGE_PROTOCOL_VERSION = "2026-06-15.phase3";

export const Integration = Object.freeze({
  APP_SERVER: "app_server",
  SDK: "sdk",
  EXEC: "exec"
});

export const RunStatus = Object.freeze({
  QUEUED: "queued",
  CLAIMED: "claimed",
  DOWNLOADING_INPUT: "downloading_input",
  STARTING_CODEX: "starting_codex",
  RUNNING: "running",
  WAITING_APPROVAL: "waiting_approval",
  UPLOADING_FINAL: "uploading_final",
  RECONCILING: "reconciling",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  CANCELLED: "cancelled",
  INTERRUPTED: "interrupted",
  HOST_UNAVAILABLE: "host_unavailable",
  NEEDS_RESUME: "needs_resume",
  EXPIRED: "expired"
});

export const TerminalRunStatuses = new Set([
  RunStatus.SUCCEEDED,
  RunStatus.FAILED,
  RunStatus.CANCELLED,
  RunStatus.INTERRUPTED,
  RunStatus.EXPIRED
]);

export const UsageStatus = Object.freeze({
  STARTED: "started",
  COMPLETED: "completed",
  FAILED: "failed",
  ESTIMATED: "estimated"
});

export const OutboxStatus = Object.freeze({
  PENDING: "pending",
  SENT: "sent",
  FAILED: "failed"
});

export const Role = Object.freeze({
  OWNER: "owner",
  ADMIN: "admin",
  MEMBER: "member",
  VIEWER: "viewer"
});

export const AgentMessageType = Object.freeze({
  HELLO: "agent.hello",
  HEARTBEAT: "agent.heartbeat",
  CAPABILITIES: "agent.capabilities",
  JOB_AVAILABLE: "job.available",
  JOB_CLAIM: "job.claim",
  JOB_CLAIMED: "job.claimed",
  RUN_STATUS: "run.status",
  EVENT_PART_UPLOADED: "run.eventPartUploaded",
  APPROVAL_REQUESTED: "run.approvalRequested",
  COMPLETED: "run.completed",
  FAILED: "run.failed",
  RECOVERY_REPORT: "run.recoveryReport"
});

export const ApprovalKind = Object.freeze({
  COMMAND: "command",
  NETWORK: "network",
  FILESYSTEM: "filesystem",
  TOOL: "tool"
});

export const DEFAULTS = Object.freeze({
  tenantIdPrefix: "ten",
  otpTtlSeconds: 300,
  otpLength: 6,
  otpMaxAttempts: 5,
  otpSendIntervalSeconds: 60,
  otpMaxPerPhonePerHour: 5,
  otpMaxPerIpPerHour: 30,
  accessTokenTtlSeconds: 900,
  refreshTokenTtlDays: 30,
  runLeaseSeconds: 60,
  leaseScanIntervalSeconds: 15,
  hostOfflineAfterSeconds: 90,
  approvalTimeoutSeconds: 600,
  maxConcurrentRunsPerHost: 1,
  eventFlushIntervalMs: 1000,
  maxEventsPerPart: 100
});

export function nowIso() {
  return new Date().toISOString();
}

export function createId(prefix) {
  const suffix = globalThis.crypto?.randomUUID?.().replaceAll("-", "") ??
    `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}_${suffix.slice(0, 24)}`;
}

export function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function requireString(record, key, label = key) {
  if (!isRecord(record) || typeof record[key] !== "string" || record[key].trim() === "") {
    throw new Error(`${label} is required`);
  }
  return record[key].trim();
}

export function optionalString(record, key) {
  if (!isRecord(record) || record[key] === undefined || record[key] === null) {
    return undefined;
  }
  if (typeof record[key] !== "string") {
    throw new Error(`${key} must be a string`);
  }
  return record[key].trim();
}

export function normalizePhoneE164(input, defaultCountryCode = "1") {
  const raw = String(input ?? "").trim();
  if (!raw) {
    throw new Error("phone is required");
  }

  if (raw.startsWith("+")) {
    const digits = raw.slice(1).replace(/\D/g, "");
    if (digits.length < 8 || digits.length > 15) {
      throw new Error("phone must be a valid E.164 number");
    }
    return `+${digits}`;
  }

  const digits = raw.replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 14) {
    throw new Error("phone must be a valid local or E.164 number");
  }
  return `+${defaultCountryCode.replace(/\D/g, "")}${digits}`;
}

export function redactPhone(phoneE164) {
  const text = String(phoneE164 ?? "");
  if (text.length <= 6) {
    return "***";
  }
  return `${text.slice(0, 3)}***${text.slice(-2)}`;
}

export function assertRunStatus(status) {
  if (!Object.values(RunStatus).includes(status)) {
    throw new Error(`invalid run status: ${status}`);
  }
  return status;
}

export function isTerminalRunStatus(status) {
  return TerminalRunStatuses.has(status);
}

export function assertTerminalTransition(current, next) {
  assertRunStatus(next);
  if (isTerminalRunStatus(current) && current !== next) {
    throw new Error(`terminal run ${current} cannot transition to ${next}`);
  }
  return true;
}

export function objectKeyForJobInput(tenantId, jobId) {
  return `tenants/${tenantId}/jobs/${jobId}/input.json`;
}

export function objectKeyForPrompt(tenantId, jobId) {
  return `tenants/${tenantId}/jobs/${jobId}/prompt.txt`;
}

export function objectKeyForAttachment(tenantId, jobId, fileId, name = "attachment") {
  const safeName = String(name ?? "attachment")
    .split(/[\\/]/)
    .pop()
    .replace(/[^A-Za-z0-9._ -]/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 120) || "attachment";
  return `tenants/${tenantId}/jobs/${jobId}/attachments/${fileId}/${safeName}`;
}

export function objectKeyForArtifact(tenantId, runId, artifactId, name = "artifact") {
  const safeName = String(name ?? "artifact")
    .split(/[\\/]/)
    .pop()
    .replace(/[^A-Za-z0-9._ -]/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 120) || "artifact";
  const safeArtifactId = String(artifactId ?? createId("art"))
    .replace(/[^A-Za-z0-9_-]/g, "_")
    .slice(0, 80) || createId("art");
  return `tenants/${tenantId}/runs/${runId}/artifacts/${safeArtifactId}/${safeName}`;
}

export function objectKeyForEventPart(tenantId, runId, partNo, extension = "jsonl") {
  const part = String(partNo).padStart(6, "0");
  return `tenants/${tenantId}/runs/${runId}/events/${part}.${extension}`;
}

export function objectKeyForFinal(tenantId, runId) {
  return `tenants/${tenantId}/runs/${runId}/final.json`;
}

export function objectKeyForManifest(tenantId, runId) {
  return `tenants/${tenantId}/runs/${runId}/manifest.json`;
}

export function objectKeyForRawUsage(tenantId, runId, attempt) {
  return `tenants/${tenantId}/runs/${runId}/usage/attempt-${attempt}.raw.json`;
}

export function validateInputEnvelope(value) {
  if (!isRecord(value)) {
    throw new Error("input envelope must be an object");
  }
  for (const key of ["version", "tenantId", "jobId", "idempotencyKey", "conversationId", "model", "sandbox", "approvalPolicy"]) {
    requireString(value, key);
  }
  if (!Array.isArray(value.input) || value.input.length === 0) {
    throw new Error("input envelope requires at least one input item");
  }
  for (const item of value.input) {
    if (!isRecord(item) || item.type !== "text") {
      throw new Error("only text input items are supported in the POC");
    }
    requireString(item, "textObjectKey");
    requireString(item, "sha256");
  }
  if (value.attachments !== undefined && !Array.isArray(value.attachments)) {
    throw new Error("attachments must be an array");
  }
  if (value.mode !== undefined && value.mode !== "turn" && value.mode !== "review") {
    throw new Error("input envelope mode must be turn or review");
  }
  if (value.reasoningEffort !== undefined && !["minimal", "low", "medium", "high", "xhigh"].includes(value.reasoningEffort)) {
    throw new Error("input envelope reasoningEffort is invalid");
  }
  if (value.reviewTarget !== undefined) {
    validateReviewTarget(value.reviewTarget);
  }
  return value;
}

export function validateReviewTarget(value) {
  if (!isRecord(value)) {
    throw new Error("review target must be an object");
  }
  const type = requireString(value, "type", "review target type");
  if (type === "uncommittedChanges") {
    return value;
  }
  if (type === "baseBranch") {
    requireString(value, "branch", "review base branch");
    return value;
  }
  if (type === "commit") {
    requireString(value, "sha", "review commit sha");
    return value;
  }
  if (type === "custom") {
    requireString(value, "instructions", "review instructions");
    return value;
  }
  throw new Error("review target type is invalid");
}

export function validateSkybridgeEvent(event) {
  if (!isRecord(event)) {
    throw new Error("event must be an object");
  }
  if (!Number.isInteger(event.seq) || event.seq < 1) {
    throw new Error("event.seq must be a positive integer");
  }
  requireString(event, "ts");
  requireString(event, "runId");
  requireString(event, "type");
  if (!isRecord(event.payload)) {
    throw new Error("event.payload must be an object");
  }
  return event;
}

export function validateCosObjectRef(value) {
  if (!isRecord(value)) {
    throw new Error("object ref must be an object");
  }
  requireString(value, "objectKey");
  requireString(value, "sha256");
  if (!Number.isInteger(value.size) || value.size < 0) {
    throw new Error("object ref size must be a non-negative integer");
  }
  return value;
}

export function canAccessTenant(principal, tenantId, minimumRole = Role.VIEWER) {
  if (!principal || principal.tenantId !== tenantId) {
    return false;
  }
  const rank = {
    [Role.VIEWER]: 0,
    [Role.MEMBER]: 1,
    [Role.ADMIN]: 2,
    [Role.OWNER]: 3
  };
  return (rank[principal.role] ?? -1) >= (rank[minimumRole] ?? 0);
}

export function safeJsonParse(text, fallback = undefined) {
  try {
    return JSON.parse(text);
  } catch {
    return fallback;
  }
}
